import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { authMeQueryKey, fetchSession, logout as postLogout } from '../../api/auth';
import { setUnauthorizedHandler } from '../../api/client';
import { clearToken, readToken, writeToken } from './token-storage';
import type { AuthContextValue, AuthSession, SessionSnapshot } from './types';

export const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
  children: ReactNode;
  /** Test/storybook seam only — production always starts from storage. */
  initialSession?: AuthSession | null;
};

/**
 * Holds the session for the whole app.
 *
 * **Authority is never read from the token** (ADR-0013). The JWT proves
 * identity; `user` and `permissions` come from `GET /auth/me`, which the
 * backend resolves from the database on every request. That is what makes a
 * permission removal take effect immediately (Story C) and what lets the
 * frontend learn mid-session that a forced password change is pending
 * (Story I).
 *
 * The session therefore lives in TanStack Query under `['auth','me']`, not in
 * component state: any mutation that could change the caller's own authority
 * — every role mutation, and user mutations that can touch the caller's own
 * row — invalidates that key, and the whole app re-derives from the server's
 * answer. `login()` seeds the cache so a fresh sign-in costs no extra round
 * trip.
 */
export function AuthProvider({ children, initialSession = null }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => {
    if (initialSession) {
      // Seam only: pre-seed the cache so the provider starts resolved.
      queryClient.setQueryData<SessionSnapshot>(authMeQueryKey, {
        user: initialSession.user,
        permissions: initialSession.permissions,
      });
      return initialSession.token;
    }
    return readToken() ?? null;
  });

  const sessionQuery = useQuery({
    queryKey: authMeQueryKey,
    queryFn: ({ signal }) => fetchSession(signal),
    // No token means no request to make; the login page renders instead.
    enabled: token !== null,
    // A 401 already cleared the session through the client's handler, and a
    // 4xx is not retryable anyway. Anything else is offered a manual retry.
    retry: false,
    staleTime: 30_000,
  });

  const clearSession = useCallback(() => {
    clearToken();
    setToken(null);
    // Drop the session and every permission-scoped list it authorised.
    queryClient.removeQueries({ queryKey: authMeQueryKey });
    queryClient.clear();
  }, [queryClient]);

  /**
   * Best-effort audit write, then local teardown. The endpoint is stateless
   * (ADR-0013 §4) — the token stays cryptographically valid until it expires,
   * which is documented rather than hidden — so a failed call must never
   * leave the user apparently signed in.
   */
  const logout = useCallback(() => {
    if (token !== null) {
      void postLogout().catch(() => {
        // Already signing out locally; a failed audit write changes nothing.
      });
    }
    clearSession();
  }, [token, clearSession]);

  const login = useCallback(
    (session: AuthSession) => {
      writeToken(session.token);
      setToken(session.token);
      queryClient.setQueryData<SessionSnapshot>(authMeQueryKey, {
        user: session.user,
        permissions: session.permissions,
      });
    },
    [queryClient],
  );

  // A 401 from any request means the session is gone — drop it everywhere.
  // The client has already cleared the token by the time this runs, so this
  // must not attempt another network call.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  // Refetch through the client, not `sessionQuery.refetch`: the query object
  // is a new identity every render, which would make this callback — and the
  // whole context value — unstable.
  const retrySession = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: authMeQueryKey });
  }, [queryClient]);

  const user = sessionQuery.data?.user ?? null;
  const permissions = useMemo(
    () => sessionQuery.data?.permissions ?? [],
    [sessionQuery.data?.permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      permissions,
      token,
      isAuthenticated: token !== null,
      // Only "loading" while a token is held and the session has not resolved.
      isLoading: token !== null && sessionQuery.isPending,
      sessionError: token !== null && sessionQuery.isError ? sessionQuery.error : null,
      retrySession,
      login,
      logout,
    }),
    [
      user,
      permissions,
      token,
      sessionQuery.isPending,
      sessionQuery.isError,
      sessionQuery.error,
      retrySession,
      login,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
